import React, { FC, useEffect } from "react";
import { Button, Col, Drawer, Form, Row } from "antd";
import { UIButton } from "../../ui/Button";
import {
  TTemporarySubshift,
  TTemporarySubshiftCreateRequest,
  TTemporarySubshiftUpdateRequest,
} from "../../../redux-modules/declarations/maps/shifts";
import { UIInput } from "../../ui/Input";
import { useSelector } from "react-redux";
import { selectorLocationsList } from "../../../redux-modules/selectors/selectorsLocations";
import { UISelect } from "../../ui/Select";
import { selectorShiftTypeList } from "../../../redux-modules/selectors/selectorsShiftTypes";
import { UIText } from "../../ui/Text";
const { Option } = UISelect;

interface Props {
  visible: boolean;
  onClose: () => void;
  onSave: (info: TTemporarySubshiftCreateRequest | TTemporarySubshiftUpdateRequest) => void;
  isLoading: boolean;
  temporaryShiftId: string;
  // se presente, il drawer è in modalità modifica di questo sub-shift
  editingSubshift?: TTemporarySubshift;
}

export const DrawerCreateSubshift: FC<Props> = ({
  visible,
  onClose,
  onSave,
  isLoading,
  temporaryShiftId,
  editingSubshift,
}: Props): JSX.Element => {
  const [form] = Form.useForm();

  const offices = useSelector(selectorLocationsList);
  const shiftTypeList = useSelector(selectorShiftTypeList);

  // Se il sub-shift ha già iscritti, sede e orari non sono più modificabili
  // (stessa regola applicata lato backend): resta modificabile solo max_users
  const isLocked = !!editingSubshift && editingSubshift.joined_users > 0;

  useEffect(() => {
    if (!visible) return;

    if (editingSubshift) {
      form.setFieldsValue({
        office_id: editingSubshift.office_id,
        max_users: editingSubshift.max_users,
        shift_types: editingSubshift.shift_types.map(s => s.id),
      });
    } else {
      form.resetFields();
    }
  }, [visible, editingSubshift]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      if (editingSubshift) {
        const updateObj: TTemporarySubshiftUpdateRequest = {
          temporary_subshift_id: editingSubshift.id,
          max_users: values.max_users,
        };

        // sede e orari vengono inviati SOLO se non bloccati: se ci sono già
        // iscritti il backend rifiuterebbe comunque la modifica di questi campi
        if (!isLocked) {
          updateObj.office_id = values.office_id;
          updateObj.shift_types = values.shift_types;
        }

        if (onSave) onSave(updateObj);
        return;
      }

      if (!values.shift_types || values.shift_types.length === 0) {
        form.setFields([
          { name: "shift_type_picker", errors: ["Devi inserire almeno 1 turno"] }
        ]);
        return;
      } else {
        form.setFields([{ name: "shift_type_picker", errors: [] }]);
      }

      const createObj: TTemporarySubshiftCreateRequest = {
        temporary_shift_id: temporaryShiftId,
        office_id: values.office_id,
        max_users: values.max_users,
        shift_types: values.shift_types,
      };

      if (onSave) onSave(createObj);

    } catch (error) {}
  };

  return <Drawer
      title={editingSubshift ? "Modifica Sede" : "Aggiungi Sede"}
      placement="right"
      onClose={onClose}
      closable={true}
      visible={visible}
      width={400}
      footer={
        <div
          style={{
            textAlign: "right",
          }}
        >
          <UIButton onClick={onClose} style={{ marginRight: 8 }}>
            Annulla
          </UIButton>
          <UIButton onClick={handleSave} type="primary" data-cy="save-subshift-button" loading={isLoading}>
            Salva
          </UIButton>
        </div>
      }
    >
      {isLocked && (
        <UIText style={{ display: "block", marginBottom: 16 }} type="secondary">
          Ci sono già utenti iscritti a questa sede: sede e orari non sono più modificabili, puoi solo cambiare il numero massimo di posti.
        </UIText>
      )}

      <Form
        layout="vertical"
        form={form}
        onFinish={handleSave}
        data-cy="new-subshift-form"
      >
        <Row>
          <Col span={24}>
            <Form.Item
              name="office_id"
              label="Sede"
              rules={[
                { required: true, message: "Sede obbligatoria" },
              ]}
            >
              <UISelect
                showSearch
                disabled={isLocked}
                placeholder={"Nessuna selezione"}
                filterOption={(input, option) =>
                  ((option!.children as unknown) as string)
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              >
                {offices.data.map(e => (
                  <Option key={"sede_"+e.id} value={e.id}>
                    {e.name}
                  </Option>
                ))}
              </UISelect>
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={24}>
            <Form.Item
              name="max_users"
              label="Numero di dipendenti"
              rules={[
                { required: true, message: "Numero obbligatorio" },
                 { type: "number", min: 1, message: "Almeno 1 dipendente" },
                 { type: "number", max: 1000, message: "Massimo 1000 dipendenti" },
              ]}
              getValueFromEvent={(e) => Number(e.target.value)}
            >
              <UIInput type="number" step={"1"} min={1} />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={24}>
            <Form.Item
            name="shift_type_picker"
            label="* Seleziona turni di lavoro"
            >
              <UISelect
                showSearch
                disabled={isLocked}
                placeholder="Seleziona turno"
                onChange={(value) => {
                  const current = form.getFieldValue("shift_types") || [];

                  if (!current.includes(value)) {
                    const newList = [...current, value];
                    // Ordinamento in base a time_from in shiftTypeList
                    newList.sort((a, b) => {
                      const shiftA = shiftTypeList.find(s => s.id === a);
                      const shiftB = shiftTypeList.find(s => s.id === b);
                      if (!shiftA || !shiftB) return 0;
                      return shiftA.time_from.localeCompare(shiftB.time_from);
                    });

                    form.setFieldsValue({ shift_types: newList });
                  }

                  form.setFieldsValue({ shift_type_picker: null });
                }}
              >
                {shiftTypeList.filter(type => parseInt(type.working_minutes) > 1).map(e => (
                  <Option key={"shiftTypeList_" + e.id} value={e.id}>
                    {e.name + " " + (e.is_notturno ? "(Notturno)" : "")}
                  </Option>
                ))}
              </UISelect>
            </Form.Item>

            <Form.List name="shift_types">
              {(fields, { remove }) => (
                <>
                  {fields.map((field, index) => {
                    const id = form.getFieldValue("shift_types")[index];
                    const shift = shiftTypeList.find(s => s.id === id);

                    return (
                      <Row key={field.key} align="middle" style={{ marginBottom: 8 }}>
                        <Col flex="auto">
                          {shift?.name} {shift?.is_notturno ? "(Notturno)" : ""}
                        </Col>

                        {!isLocked && (
                          <Col>
                            <Button danger onClick={() => remove(field.name)}>
                              Elimina
                            </Button>
                          </Col>
                        )}
                      </Row>
                    );
                  })}
                </>
              )}
            </Form.List>
          </Col>
        </Row>
      </Form>
    </Drawer>;
}